import React, { useState } from 'react';
import { useLanguage } from "../../contexts/LanguageContext";
import { postApi } from '../../utils/api/api';
import importImage from "../../utils/images/importImage";
import { showToast } from '../../utils/toast';

const ForgotPasswordForm = () => {
  const ForgotPasswordImage = importImage("login-form.jpg");
  const { t } = useLanguage();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({ email: '' });

  const validateForm = () => {
    let formErrors = {};
    let isValid = true;

    if (!email) {
      formErrors.email = t('errors.emailRequired');
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      formErrors.email = t('errors.invalidEmail');
      isValid = false;
    }

    setErrors(formErrors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      const response = await postApi('/forgot-password', { email });
      const message = response?.message || t('success.forgotPassword', 'Password reset email sent!');
      showToast('success', message);
    } catch (err) {
      const errorMessage = err.response?.data?.data || t('errors.forgotPasswordFailed', 'Failed to send reset email.');
      setErrors({ ...errors, email: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="forgot-password-page bg-light min-vh-100 d-flex align-items-center">
      <div className="container">
        <div className="row g-0">
          {/* Combined Card - Form + Image */}
          <div className="col-12 col-lg-12">
            <div className="card shadow-lg border-0 overflow-hidden" style={{ 
              borderRadius: '20px',
              border: '1px solid rgba(0,0,0,0.1)',
              boxShadow: '0 15px 30px rgba(0,0,0,0.12)'
            }}>
              <div className="row g-0 h-100">
                {/* Form Column */}
                <div className="col-lg-5 d-flex align-items-center">
                  <div className="card-body p-4 p-md-5">
                    <div className="text-center mb-4">
                      <h2 className="text-primary fw-bold">
                        {t('titles.forgotPassword', 'Reset Your Password')}
                      </h2>
                      <p className="text-muted">
                        {t('forgotPassword.subheading', 'Enter your email to receive a reset link')}
                      </p>
                    </div>

                    <form onSubmit={handleSubmit} className="needs-validation" noValidate>
                      <div className="mb-4">
                        <label htmlFor="email" className="form-label fw-semibold">
                          {t('forgotPassword.emailLabel', 'Email Address')}
                          <span className="text-danger">*</span>
                        </label>
                        <div className="input-group has-validation">
                          <span className="input-group-text bg-light border-end-0">
                            <i className="bi bi-envelope text-muted"></i>
                          </span>
                          <input
                            id="email"
                            type="email"
                            name="email"
                            className={`form-control py-3 ${errors.email ? 'is-invalid' : ''}`}
                            placeholder={t('placeholders.email', 'your@email.com')}
                            value={email}
                            onChange={(e) => {
                              setEmail(e.target.value);
                              setErrors({ ...errors, email: '' });
                            }}
                            required
                          />
                          {errors.email && (
                            <div className="invalid-feedback">{errors.email}</div>
                          )}
                        </div>
                      </div>

                      <div className="d-grid mb-3">
                        <button
                          type="submit"
                          className="btn btn-primary py-3 fw-semibold"
                          disabled={loading}
                        >
                          {loading ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                              {t('buttons.sending', 'Sending...')}
                            </>
                          ) : (
                            t('buttons.send', 'Send Reset Link')
                          )}
                        </button>
                      </div>

                      <div className="text-center mt-4 pt-3 border-top">
                        <p className="mb-0">
                          {t('forgotPassword.rememberPassword', 'Remember your password?')}{' '}
                          <a href="/login" className="text-primary fw-semibold text-decoration-none">
                            {t('forgotPassword.login', 'Login here')}
                          </a>
                        </p>
                      </div>
                    </form>
                  </div>
                </div>

                {/* Image Column */}
                <div className="col-lg-7 d-none d-lg-flex position-relative">
                  <div className="h-100 w-100" style={{
                    backgroundImage: `url(${ForgotPasswordImage})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    borderLeft: '1px solid rgba(0,0,0,0.1)',
                    borderTopRightRadius: '20px',
                    borderBottomRightRadius: '20px',
                    position: 'relative',
                    overflow: 'hidden'
                  }}>
                    <div className="position-absolute top-0 start-0 w-100 h-100" style={{
                      background: 'linear-gradient(135deg, rgba(13,110,253,0.15) 0%, rgba(255,255,255,0) 100%)'
                    }}></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordForm;